import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionScheduleDetail } from './hooks/useConstructionScheduleDetail';
import { ScheduleStatusBadge } from './components/ScheduleStatusBadge';
import { Card, Button, Tabs, Badge, Avatar, Alert } from '@/components/ui';

export default function ScheduleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { schedule, isLoading, updateSchedule, isUpdating } = useConstructionScheduleDetail(id);

  if (isLoading) return <div>Đang tải...</div>;
  if (!schedule) return <div>Không tìm thấy lịch thi công</div>;

  const handleStatusUpdate = async (newStatus: string) => {
    await updateSchedule({ status_code: newStatus as any });
  };

  return (
    <PageDetailLayout
      title={schedule.title}
      subtitle={schedule.schedule_code}
      breadcrumbs={[
        { label: 'Thi công' },
        { label: 'Lịch trình', href: '/construction/schedules' },
        { label: schedule.schedule_code }
      ]}
      headerExtra={<ScheduleStatusBadge status={schedule.status_code} className="px-3 py-1 text-xs" />}
      actions={
        <div className="flex gap-2">
          {schedule.status_code === 'scheduled' && (
             <Button variant="primary" onClick={() => handleStatusUpdate('active')} isLoading={isUpdating}>Bắt đầu triển khai</Button>
          )}
          <Button variant="outline" icon="ri-edit-line">Chỉnh sửa</Button>
          <Button variant="outline" icon="ri-calendar-event-line">Dời lịch</Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
           <Card className="p-6">
              <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                <i className="ri-information-line text-slate-400" />
                Thông tin kế hoạch
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12">
                 <div className="space-y-1">
                    <p className="text-[10px] font-bold text-slate-400  tracking-wider">Loại công tác</p>
                    <Badge variant="custom" className="bg-slate-50 text-slate-700">{schedule.work_type?.toUpperCase()}</Badge>
                 </div>
                 <div className="space-y-1">
                    <p className="text-[10px] font-bold text-slate-400  tracking-wider">Dự án & Giai đoạn</p>
                    <p className="text-sm font-medium text-slate-900">{schedule.project?.name} / {schedule.phase?.name || '---'}</p>
                 </div>
                 <div className="space-y-1">
                    <p className="text-[10px] font-bold text-slate-400  tracking-wider">Ngày bắt đầu (Dự kiến)</p>
                    <p className="text-sm font-bold text-slate-900">{schedule.scheduled_start_date}</p>
                 </div>
                 <div className="space-y-1">
                    <p className="text-[10px] font-bold text-slate-400  tracking-wider">Ngày kết thúc (Dự kiến)</p>
                    <p className="text-sm font-bold text-slate-900">{schedule.scheduled_end_date}</p>
                 </div>
                 <div className="md:col-span-2 space-y-1">
                    <p className="text-[10px] font-bold text-slate-400  tracking-wider">Mô tả công việc</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{schedule.description || 'Không có mô tả chi tiết.'}</p>
                 </div>
              </div>
           </Card>

           <Tabs
             tabs={[
               { id: 'tasks', label: 'Danh sách công việc', icon: 'ri-list-check' },
               { id: 'resources', label: 'Nguồn lực & Vật tư', icon: 'ri-stack-line' },
               { id: 'gantt', label: 'Biểu đồ Gantt', icon: 'ri-bar-chart-horizontal-line' },
               { id: 'attachments', label: 'Tài liệu đính kèm', icon: 'ri-attachment-line' },
             ]}
           >
             <div className="py-12 text-center text-slate-400 border border-dashed border-slate-100 rounded-xl mt-4">
                <i className="ri-calendar-line text-4xl mb-2 block opacity-20" />
                <p className="text-xs">Tính năng đang được cập nhật cho phiên bản 4.0.</p>
             </div>
           </Tabs>
        </div>

        <div className="space-y-6">
           <Card className="p-6">
              <h4 className="font-bold text-slate-900 mb-6 flex items-center justify-between">
                Đơn vị thi công
                <Badge className="bg-slate-100 text-slate-600 border-none italic">ASSIGNED</Badge>
              </h4>
              <div className="space-y-6">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-os-primary/10 flex items-center justify-center text-os-primary">
                       <i className="ri-team-line text-2xl" />
                    </div>
                    <div>
                       <p className="text-sm font-black text-slate-900">{schedule.team?.name}</p>
                       <p className="text-[10px] font-bold text-slate-400  tracking-widest">{schedule.team?.code}</p>
                    </div>
                 </div>
                 <div className="pt-4 border-t border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400  mb-3">Người phụ trách (Foreman)</p>
                    <div className="flex items-center gap-3">
                       <Avatar src={schedule.foreman?.avatar_url} fallback={schedule.foreman?.full_name?.charAt(0)} size="sm" />
                       <p className="text-xs font-black text-slate-700">{schedule.foreman?.full_name}</p>
                    </div>
                 </div>
              </div>
           </Card>

           <Card className="p-6 bg-slate-900 text-white border-none">
              <h4 className="font-bold opacity-60 text-[10px]  tracking-widest mb-6">Phân tích rủi ro</h4>
              <div className="space-y-4">
                 <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-emerald-400 mt-0.5" />
                    <p className="text-xs opacity-80">Thời tiết thuận lợi cho công tác đổ bê tông.</p>
                 </div>
                 <div className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-amber-400 mt-0.5" />
                    <p className="text-xs opacity-80">Cần lưu ý việc cấp vật tư thép cho Zone B vào ngày 15/05.</p>
                 </div>
              </div>
           </Card>

           <Alert variant="info" className="bg-blue-50 border-blue-100">
              <p className="text-[10px] text-blue-700">
                 Lịch thi công này đã được đồng bộ với <b>Master Schedule</b> của dự án.
              </p>
           </Alert>
        </div>
      </div>
    </PageDetailLayout>
  );
}
